import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import { useTranslation } from '../i18n.js'
import { MoneyInputError, parseMajorInput } from '../money.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import {
  stockReasonLabel,
  STOCK_REASONS_ADDING,
  STOCK_REASONS_TAKING_AWAY,
} from '../status-labels.js'
import type { StockPositionData } from './Stock.js'

/**
 * Booking stock in and correcting it. §6.10 `INV-001`, `INV-005`. Posts to `POST /stock/receipts`
 * and `POST /stock/items/:stockItemId/adjustments`.
 *
 * **`Stock.tsx` is read-only on purpose and this is the half it named.** Its docstring splits the
 * fifteen stock routes into three jobs: what is here, receiving and adjusting, and counting. The
 * first is that screen, this is the second, and counting stays blocked because `stock_adjust` is
 * owner and manager only while a stocktake is a thing a dispenser does with a clipboard
 * (`later_todo.md` 27).
 *
 * **Both panels work from a position already on the shelf, and that is a limit, not a design.**
 * `ReceiveStock` needs a `frameVariantId` or a `lensProductId`, and the only place the counter can
 * get either is a `StockPositionData` row, which is to say something the shop already stocks. So
 * this receives *more of this*, which is the delivery a shop takes most days, and it cannot receive
 * a frame the shop has never carried: there is no frames catalogue read in the product at all. The
 * screen says so rather than offering a box for an id nobody can look up, and the gap is recorded.
 *
 * **Two rows, two panels, and neither borrows the other's.** Receiving is `stock_receive` (owner,
 * manager, dispenser) and adjusting is `stock_adjust` (owner and manager, sensitive), which is the
 * separation that makes a correction somebody's decision rather than part of putting a delivery
 * away. A dispenser books the delivery in and cannot write off the breakage.
 *
 * **Every refusal is pre-checked, because a refused write stops the whole queue** (`OFF-001a`):
 *
 * - A quantity that is not whole pieces, one or more, which `asQuantity` refuses.
 * - A delta of zero, which `INV-005` makes meaningless: an adjustment that changes nothing is a
 *   reason with no movement, and the service refuses it.
 * - An adjustment with no reason. `INV-005` requires one and `ROLE-006`'s override report is read
 *   by somebody who was not there, so stock that vanished with no explanation is the shape a
 *   shrinkage problem hides in.
 * - A cost on consignment stock, which `INV-003` makes a contradiction: the shop does not own it,
 *   so it has no cost to carry. Refused here with the sentence rather than sent.
 * - A cost missing on owned stock, where the service needs it to value the receipt.
 */

export interface StockWritesProps {
  /** The position being received into or corrected, from the row the screen is showing. */
  readonly position: StockPositionData
  readonly locationId: string
  /** `stock_receive`: owner, manager, dispenser. */
  readonly canReceive: boolean
  /** `stock_adjust`: owner and manager, and Appendix B marks it sensitive. */
  readonly canAdjust: boolean
  /** Called once the server has taken either write, so the shelf can be read again. */
  readonly onWritten?: () => void
}

/** `INV-003`: consignment stock is not the shop's, so it carries no cost. */
const isConsignment = (ownership: string): boolean => ownership === 'consignment'

export function StockWrites({
  position,
  locationId,
  canReceive,
  canAdjust,
  onWritten,
}: StockWritesProps): JSX.Element | null {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [writeId, setWriteId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [received, setReceived] = useState('')
  const [unitCost, setUnitCost] = useState('')
  const [delta, setDelta] = useState('')
  const [reason, setReason] = useState('')

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setReceived('')
    setUnitCost('')
    setDelta('')
    setReason('')
    onWritten?.()
  })

  const consignment = isConsignment(position.ownership)
  /**
   * Which reasons this correction may carry, which depends on its direction.
   *
   * `applyMovement` allows `count_variance` alone for a positive delta: pieces appearing can only
   * be a count that was wrong, where pieces leaving can be any of the losses. Offering the losses
   * on a positive delta would queue a 422 that holds the till, and hiding the distinction would
   * leave somebody choosing "Damaged" for two frames they just found.
   */
  const adding = Number(delta.trim()) > 0
  const reasonsOffered = adding ? STOCK_REASONS_ADDING : STOCK_REASONS_TAKING_AWAY

  /** Whole pieces, one or more, which is what `asQuantity` takes. */
  const pieces = useCallback(
    (text: string): number | null => {
      const value = Number(text.trim())
      if (text.trim() === '' || !Number.isSafeInteger(value) || value < 1) {
        setError(t('stockWrites.quantityWhole'))
        return null
      }
      return value
    },
    [t],
  )

  const receive = useCallback(async () => {
    const quantity = pieces(received)
    if (quantity === null) return

    let unitCostMinor: string | undefined
    if (consignment) {
      if (unitCost.trim() !== '') {
        // `INV-003`. The service refuses a cost on consignment outright, and it is a 422 that
        // would hold up the till rather than a figure to quietly drop.
        setError(t('stockWrites.consignmentHasNoCost'))
        return
      }
    } else {
      if (unitCost.trim() === '') {
        setError(t('stockWrites.costRequired'))
        return
      }
      try {
        unitCostMinor = parseMajorInput(unitCost, position.currency).toString()
      } catch (inputError) {
        setError(
          inputError instanceof MoneyInputError ? inputError.message : t('stockWrites.costInvalid'),
        )
        return
      }
    }

    setError(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/stock/receipts',
      label: t('stockWrites.queueReceiveLabel'),
      body: {
        locationId,
        // The ids come off the position, which is what makes this "more of this" rather than a
        // catalogue lookup the counter cannot do.
        ...(position.frameVariantId === undefined
          ? {}
          : { frameVariantId: position.frameVariantId }),
        ...(position.lensProductId === undefined ? {} : { lensProductId: position.lensProductId }),
        ownership: position.ownership,
        lot: position.lot,
        ...(position.expiryDate === undefined ? {} : { expiryDate: position.expiryDate }),
        ...(position.tray === undefined ? {} : { tray: position.tray }),
        quantity,
        ...(unitCostMinor === undefined ? {} : { unitCostMinor }),
        // Appendix J's lock. The receipt adds to a row that exists, so a stale version means
        // somebody else moved this shelf while the form was open.
        expectedVersion: position.version,
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [consignment, enqueue, locationId, pieces, position, received, t, unitCost])

  const adjust = useCallback(async () => {
    const value = Number(delta.trim())
    if (delta.trim() === '' || !Number.isSafeInteger(value)) {
      setError(t('stockWrites.deltaWhole'))
      return
    }
    if (value === 0) {
      // `INV-005`: an adjustment that changes nothing is a reason attached to no movement.
      setError(t('stockWrites.deltaNotZero'))
      return
    }
    if (reason === '') {
      setError(t('stockWrites.reasonRequired'))
      return
    }

    let unitCostMinor: string | undefined
    if (value > 0 && !consignment && position.quantity === 0) {
      // `asMinor` on found stock in an empty owned position: there is no cost on the shelf to
      // carry forward, so the service needs one and refuses without it.
      if (unitCost.trim() === '') {
        setError(t('stockWrites.foundCostRequired'))
        return
      }
      try {
        unitCostMinor = parseMajorInput(unitCost, position.currency).toString()
      } catch (inputError) {
        setError(
          inputError instanceof MoneyInputError ? inputError.message : t('stockWrites.costInvalid'),
        )
        return
      }
    }

    setError(null)
    const write = (await enqueue({
      method: 'POST',
      path: `/stock/items/${position.stockItemId}/adjustments`,
      label: t('stockWrites.queueAdjustLabel'),
      body: {
        expectedVersion: position.version,
        delta: value,
        // An Appendix E code, never prose. `asReason` refuses anything else with a 422, and the
        // chooser offers only what `applyMovement` allows for this direction, so the pair cannot
        // disagree.
        reason,
        ...(unitCostMinor === undefined ? {} : { unitCostMinor }),
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [consignment, delta, enqueue, position, reason, t, unitCost])

  const line = statusLine(t, snapshot, writeId, t('queue.subject.stockWrite'))

  // The second lock. `Stock.tsx` does not mount this panel for a reader who holds neither row,
  // which is the lock that matters, because the hooks above run before any early return and a
  // read-only screen must not need the write queue at all. This one is for a caller that renders
  // the panel directly.
  if (!canReceive && !canAdjust) return null

  return (
    <section className="lm-stock-writes" aria-labelledby="stock-writes-heading">
      <h3 id="stock-writes-heading" className="lm-stock-writes__heading">
        {t('stockWrites.heading')}
      </h3>
      <p className="lm-hint">{t('stockWrites.moreOfThisOnly')}</p>

      {canReceive ? (
        <fieldset className="lm-stock-writes__panel">
          <legend>{t('stockWrites.receiveLegend')}</legend>
          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('stockWrites.receivedLabel')}</span>
            <input
              className="lm-input"
              dir="ltr"
              inputMode="numeric"
              autoComplete="off"
              value={received}
              onChange={(e) => {
                setReceived(e.currentTarget.value)
              }}
            />
          </label>
          {consignment ? (
            <p className="lm-hint">{t('stockWrites.consignmentMeans')}</p>
          ) : (
            <label className="lm-field lm-field--inline">
              <span className="lm-field__label">
                {t('stockWrites.unitCostLabel', { currency: position.currency })}
              </span>
              <input
                className="lm-input"
                dir="ltr"
                inputMode="decimal"
                autoComplete="off"
                value={unitCost}
                onChange={(e) => {
                  setUnitCost(e.currentTarget.value)
                }}
              />
            </label>
          )}
          <button
            type="button"
            className="lm-button lm-button--primary"
            disabled={writeId !== null}
            onClick={() => {
              void receive()
            }}
          >
            {t('stockWrites.receive')}
          </button>
        </fieldset>
      ) : null}

      {canAdjust ? (
        <fieldset className="lm-stock-writes__panel">
          <legend>{t('stockWrites.adjustLegend')}</legend>
          <p className="lm-hint">{t('stockWrites.adjustMeans')}</p>
          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('stockWrites.deltaLabel')}</span>
            <input
              className="lm-input"
              dir="ltr"
              inputMode="numeric"
              autoComplete="off"
              value={delta}
              onChange={(e) => {
                setDelta(e.currentTarget.value)
              }}
            />
          </label>
          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('stockWrites.reasonLabel')}</span>
            <select
              className="lm-input"
              value={reason}
              onChange={(e) => {
                setReason(e.currentTarget.value)
              }}
            >
              <option value="">{t('stockWrites.reasonPick')}</option>
              {reasonsOffered.map((code) => (
                <option key={code} value={code}>
                  {stockReasonLabel(t, code)}
                </option>
              ))}
            </select>
          </label>
          {adding ? <p className="lm-hint">{t('stockWrites.reasonOneWay')}</p> : null}
          {!consignment && position.quantity === 0 ? (
            <label className="lm-field lm-field--inline">
              <span className="lm-field__label">
                {t('stockWrites.foundCostLabel', { currency: position.currency })}
              </span>
              <input
                className="lm-input"
                dir="ltr"
                inputMode="decimal"
                autoComplete="off"
                value={unitCost}
                onChange={(e) => {
                  setUnitCost(e.currentTarget.value)
                }}
              />
            </label>
          ) : null}
          <button
            type="button"
            className="lm-button lm-button--danger"
            disabled={writeId !== null}
            onClick={() => {
              void adjust()
            }}
          >
            {t('stockWrites.adjust')}
          </button>
        </fieldset>
      ) : (
        <p className="lm-hint">{t('stockWrites.adjustNeedsManager')}</p>
      )}

      {error === null ? null : (
        <p className="lm-error" role="alert">
          {error}
        </p>
      )}
      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}
    </section>
  )
}
